import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { getFavorites, unfavoriteWord } from '../store/authSlice';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background: rgba(30, 30, 30, 0.95);
  border-radius: 16px;
  padding: 32px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(20px);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: #ffffff;
  font-size: 16px;
  margin-bottom: 20px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const FavoritesList = styled.div`
  overflow-y: auto;
  flex: 1;
  margin: 0 -8px;
  padding: 0 8px;
`;

const FavoriteItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  border-left: 4px solid #667eea;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-2px);
  }
`;

const FavoriteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const WordTitle = styled.h3`
  color: #ffffff;
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PhoneticText = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
  font-size: 0.9rem;
`;

const DefinitionText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  margin: 8px 0 0 0;
  line-height: 1.5;
  font-size: 0.95rem;
`;

const RemoveButton = styled.button`
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(239, 68, 68, 0.2);
    color: #ffffff;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.6);
  
  h3 {
    margin-bottom: 8px;
    color: rgba(255, 255, 255, 0.8);
  }
  
  p {
    margin: 0;
    font-size: 0.95rem;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #ffffff;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);

const PlayButton = styled.button`
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  margin-left: 8px;
  
  &:hover {
    background: rgba(102, 126, 234, 0.2);
    color: #5a6fd8;
  }
  
  &:disabled {
    background: #555;
    cursor: not-allowed;
    opacity: 0.5;
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z"/>
  </svg>
);

const FavoritesModal = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [audioRef, setAudioRef] = useState(null);
  
  const dispatch = useDispatch();
  const { favorites, loading } = useSelector(state => state.auth);

  useEffect(() => {
    if (isOpen) {
      dispatch(getFavorites());
    }
  }, [dispatch, isOpen]);

  const handleRemoveFavorite = async (favoriteId) => {
    try {
      await dispatch(unfavoriteWord(favoriteId)).unwrap();
    } catch (error) {
      console.error('取消收藏失败:', error);
      alert('取消收藏失败，请稍后重试');
    }
  };

  const handlePlayAudio = (audioUrl) => {
    if (!audioUrl) return;
    
    // 停止之前可能正在播放的音频
    if (audioRef) {
      audioRef.pause();
      audioRef.currentTime = 0;
    }
    
    // 创建新的音频对象
    const newAudio = new Audio(audioUrl);
    setAudioRef(newAudio);
    
    // 播放音频
    newAudio.play().catch(err => {
      console.error('播放音频失败:', err);
    });
    
    // 监听播放结束事件
    newAudio.onended = () => {
      setAudioRef(null);
    };
  };

  // 过滤收藏列表
  const filteredFavorites = favorites.filter(fav => 
    fav.word.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fav.definition.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>我的收藏</ModalTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <SearchInput
          type="text"
          placeholder="搜索单词或定义..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#ffffff' }}>
            <LoadingSpinner />
            加载中...
          </div>
        ) : filteredFavorites.length === 0 ? (
          <EmptyState>
            <h3>{searchTerm ? '没有找到匹配的收藏' : '暂无收藏'}</h3>
            <p>{searchTerm ? '尝试使用其他关键词搜索' : '开始收藏你感兴趣的单词吧！'}</p>
          </EmptyState>
        ) : (
          <FavoritesList>
            {filteredFavorites.map((favorite) => (
              <FavoriteItem key={favorite.id}>
                <FavoriteHeader>
                  <WordTitle>
                    {favorite.word}
                    {favorite.phonetic && (
                      <PhoneticText>{favorite.phonetic}</PhoneticText>
                    )}
                    {favorite.audioUrl && (
                      <PlayButton 
                        onClick={() => handlePlayAudio(favorite.audioUrl)}
                        title="播放发音"
                      >
                        <PlayIcon />
                      </PlayButton>
                    )}
                  </WordTitle>
                  <RemoveButton 
                    onClick={() => handleRemoveFavorite(favorite.id)}
                    title="取消收藏"
                  >
                    删除
                  </RemoveButton>
                </FavoriteHeader>
                <DefinitionText>{favorite.definition}</DefinitionText>
              </FavoriteItem>
            ))}
          </FavoritesList>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default FavoritesModal;